import React, { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigationType, useNavigate } from 'react-router-dom';
import { BadgePercent, Settings, LayoutDashboard, GanttChartSquare, Newspaper, Loader2, DollarSign, LifeBuoy, Radio, House } from 'lucide-react';
import { createPageUrl } from '@/utils';
import { Toaster } from '@/components/ui/toaster';
import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/ui/button';
import LanguageSelector from '../i18n/LanguageSelector';
import { useTranslation } from '@/components/i18n/TranslationProvider';
import { useAuth } from '@/context/AuthContext.jsx';
import { useToast } from '@/components/ui/use-toast';
import { useLoginDrawer } from '@/context/LoginDrawerContext.jsx';
import { useDynamicPanel } from '@/context/DynamicPanelContext.jsx';
import { usePublication } from '@/context/PublicationContext.jsx';
import LoginDrawerTrigger from './LoginDrawerTrigger.jsx';
import AuthLegalNotice from '@/components/auth/AuthLegalNotice.jsx';
import LegalAccountNotice from '@/components/legal/LegalAccountNotice.jsx';
import SiteHeader from './SiteHeader.jsx';
import CookieControls from './CookieControls.jsx';
import BackToTop from './BackToTop.jsx';
import { AVATAR_TYPES, buildGeneratedAvatar } from '@/lib/avatar.js';
import useTelemetrySetup from '@/hooks/useTelemetrySetup.js';
import useCookieConsentUi from '@/hooks/useCookieConsentUi.js';
import useLayoutNavigationState from '@/hooks/useLayoutNavigationState.js';
import useDrawerNavigationSync from '@/hooks/useDrawerNavigationSync.js';
import useIntegrationStatusNotifications from '@/hooks/useIntegrationStatusNotifications.js';
import { trackTelemetry } from '@/lib/telemetryClient.js';
import {
    PUBLIC_TELEMETRY_EVENTS,
    resolvePublicTelemetrySurface,
} from '@/lib/publicTelemetryContract.js';
import {
    buildContextualPanelModel,
    shouldEnableContextualDrawer,
} from './contextualPanelModel.js';
import { shouldUseSelectivePublicAccountMode } from '@/lib/selectiveAccountMode.js';
import { signOutAndClearPublicAuthIntent } from '@/lib/publicAuthSession.js';
import {
    buildLayoutNavigationItems,
    HEADER_NAV_ICON_IDS,
    resolveLayoutActiveNavHref,
} from './layoutNavigationModel.js';

const LoginDrawer = lazy(() => import('./LoginDrawer.jsx'));

const HEADER_NAV_ICONS = Object.freeze({
    [HEADER_NAV_ICON_IDS.HOME]: House,
    [HEADER_NAV_ICON_IDS.GAMES]: GanttChartSquare,
    [HEADER_NAV_ICON_IDS.DEAL_FINDER]: BadgePercent,
    [HEADER_NAV_ICON_IDS.DASHBOARD]: LayoutDashboard,
    [HEADER_NAV_ICON_IDS.LIVE]: Radio,
    [HEADER_NAV_ICON_IDS.NEWS]: Newspaper,
    [HEADER_NAV_ICON_IDS.SUPPORT]: LifeBuoy,
    [HEADER_NAV_ICON_IDS.PRICING]: DollarSign,
    [HEADER_NAV_ICON_IDS.DATA_MANAGER]: Settings,
});

function LayoutShell({ children }) {
    const { t } = useTranslation();
    const location = useLocation();
    const navigationType = useNavigationType();
    const navigate = useNavigate();
    const { user, signOut, signIn, permissions } = useAuth();
    const { toast } = useToast();
    const {
        isOpen: isDrawerOpen,
        open: openDrawer,
        close: closeDrawer,
        authIntent: drawerAuthIntent,
        registerRouteChange,
    } = useLoginDrawer();
    const { gamePanelData } = useDynamicPanel();
    const { isSelective, isPrivilegedUser } = usePublication();
    const [isSigningOut, setIsSigningOut] = useState(false);
    const [isGuestGooglePending, setIsGuestGooglePending] = useState(false);
    const [hasAvatarLoadError, setHasAvatarLoadError] = useState(false);
    const [shouldRenderLoginDrawer, setShouldRenderLoginDrawer] = useState(false);
    const {
        isMenuOpen,
        isAccountMenuOpen,
        toggleMenu,
        closeMenu,
        toggleAccountMenu,
        accountMenuRef,
        closeAccountMenu,
    } = useLayoutNavigationState(location, navigationType);
    const {
        isCookieBannerVisible,
        isCookiePreferencesOpen,
        handleOpenCookiePreferences,
        handleCloseCookiePreferences,
        consentCategories,
    } = useCookieConsentUi();
    useTelemetrySetup();

    const landingHref = useMemo(() => createPageUrl('Landing'), []);
    const isDashboardRoute = location.pathname.toLowerCase().startsWith('/dashboard');
    const isOfficialLiveRoute = location.pathname.toLowerCase().startsWith('/live');
    const isSelectivePublicAccount = shouldUseSelectivePublicAccountMode({
        isSelective,
        isPrivilegedUser,
    });
    const isContextualDrawerEnabled = shouldEnableContextualDrawer({
        pathname: location.pathname,
        isSelectivePublic: isSelectivePublicAccount,
    });
    const shouldDisableDrawer =
        (Boolean(user) && isDashboardRoute) || !isContextualDrawerEnabled;
    const shouldMaskDashboard = !user && isDashboardRoute && isDrawerOpen;
    const shouldHideEdgeDrawerTrigger = isOfficialLiveRoute;
    const activeNavHref = useMemo(
        () => resolveLayoutActiveNavHref({
            pathname: location.pathname,
            isSelectivePublicNavigation: isSelectivePublicAccount,
        }),
        [isSelectivePublicAccount, location.pathname],
    );

    const handleGuestGoogleSignIn = useCallback(async () => {
        if (isGuestGooglePending) {
            return;
        }
        setIsGuestGooglePending(true);
        try {
            await signIn({
                provider: 'google',
                options: {
                    redirectTo: typeof window !== 'undefined' ? `${window.location.origin}/dashboard` : undefined,
                    queryParams: { prompt: 'select_account' },
                },
            });
        } catch (error) {
            console.error('[LayoutShell] google sign-in failed', error);
            toast({
                variant: 'destructive',
                title: t('auth.sign_in_error'),
                description: t('dashboard.login_drawer.login_error'),
            });
            setIsGuestGooglePending(false);
        }
    }, [isGuestGooglePending, signIn, t, toast]);

    useDrawerNavigationSync({
        location,
        shouldDisableDrawer,
        isDrawerOpen,
        openDrawer,
        closeDrawer,
        user,
        registerRouteChange,
    });

    useEffect(() => {
        if (isDrawerOpen) {
            setShouldRenderLoginDrawer(true);
        }
    }, [isDrawerOpen]);

    const isAdmin = user?.role === 'admin';
    const userPermissions = Array.isArray(permissions) ? permissions : [];
    const canAccessDealFinder = isAdmin || userPermissions.includes('commerce.manage');
    const navItems = useMemo(
        () => buildLayoutNavigationItems({
            isSelectivePublicNavigation: isSelectivePublicAccount,
            isAdmin,
            canAccessDealFinder,
            translate: t,
        }).map((item) => ({
            ...item,
            icon: HEADER_NAV_ICONS[item.iconId],
        })),
        [t, isAdmin, canAccessDealFinder, isSelectivePublicAccount],
    );

    const drawerMeta = useMemo(() => {
        const dashboardGuestSections = isSelectivePublicAccount ? [] : [
            {
                id: 'google-login',
                render: (
                    <div className="space-y-3">
                        <AuthLegalNotice context="social" />
                        <button
                            type="button"
                            onClick={handleGuestGoogleSignIn}
                            disabled={isGuestGooglePending}
                            className={cn(
                                buttonVariants({ variant: 'secondary' }),
                                'w-full justify-center bg-white text-slate-900 hover:bg-slate-100 disabled:opacity-70'
                            )}
                        >
                            {isGuestGooglePending ? (
                                <span className="flex items-center justify-center gap-2">
                                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                                    {t('auth.redirecting')}
                                </span>
                            ) : (
                                t('auth.sign_in_with_google')
                            )}
                        </button>
                    </div>
                ),
            },
        ];

        return buildContextualPanelModel({
            pathname: location.pathname,
            user,
            gamePanelData,
            dashboardGuestSections,
            isSelectivePublic: isSelectivePublicAccount,
            labels: {
                comingSoonBadge: t('dashboard.common.coming_soon'),
                defaultTitle: t('dashboard.login_drawer.meta.default.title'),
                defaultDescription: t('dashboard.login_drawer.meta.default.description'),
                gameTitle: t('dashboard.login_drawer.meta.game.title'),
                gameDescription: t('dashboard.login_drawer.meta.game.description'),
                selectiveGameDescription: t('dashboard.login_drawer.meta.game.description_selective'),
                gamesTitle: t('dashboard.login_drawer.meta.games.title'),
                gamesDescription: t('dashboard.login_drawer.meta.games.description'),
                gameRelationshipTitle: t('dashboard.login_drawer.meta.game.sections.relationship_title'),
                gameRelationshipContent: t('dashboard.login_drawer.meta.game.sections.relationship_content'),
                gameDiscussionTitle: t('dashboard.login_drawer.meta.game.sections.discussion_title'),
                gameDiscussionContent: t('dashboard.login_drawer.meta.game.sections.discussion_content'),
                articleTitle: t('dashboard.login_drawer.meta.article.title'),
                articleDescription: t('dashboard.login_drawer.meta.article.description'),
                selectiveArticleDescription: t('dashboard.login_drawer.meta.article.description_selective'),
                newsTitle: t('dashboard.login_drawer.meta.news.title'),
                newsDescription: t('dashboard.login_drawer.meta.news.description'),
                articleDiscussionTitle: t('dashboard.login_drawer.meta.article.sections.active_discussion_title'),
                articleDiscussionContent: t('dashboard.login_drawer.meta.article.sections.active_discussion_content'),
                dashboardTitle: t('dashboard.login_drawer.meta.dashboard.title'),
                dashboardDescriptionAuthenticated: t('dashboard.login_drawer.meta.dashboard.description_authenticated'),
                dashboardDescriptionGuest: t('dashboard.login_drawer.meta.dashboard.description_guest'),
            },
        });
    }, [
        location.pathname,
        gamePanelData,
        t,
        user,
        handleGuestGoogleSignIn,
        isGuestGooglePending,
        isSelectivePublicAccount,
    ]);


    const highlightAvatar = Boolean(drawerMeta.highlightAvatar);

    useEffect(() => {
        if (!consentCategories?.analytics) {
            return;
        }

        const surface = resolvePublicTelemetrySurface(location.pathname);
        if (!surface) {
            return;
        }

        trackTelemetry(PUBLIC_TELEMETRY_EVENTS.SURFACE_VIEW, {
            ...surface,
            navigation_type: navigationType,
        });
    }, [location.pathname, navigationType, consentCategories?.analytics]);

    useIntegrationStatusNotifications({ location, navigate, toast });

    const displayName = user?.display_name || user?.full_name || user?.email || '';
    const initials = displayName
        .split(' ')
        .filter(Boolean)
        .map((part) => part[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();

    const avatarPresentation = useMemo(() => {
        if (!user) {
            return {
                type: AVATAR_TYPES.GENERATED,
                generated: buildGeneratedAvatar({
                    seed: null,
                    displayName: 'GameplaySync',
                    email: null,
                }),
            };
        }

        const isUploaded = user.avatar_type === AVATAR_TYPES.UPLOADED && user.avatar_url;
        if (isUploaded) {
            return {
                type: AVATAR_TYPES.UPLOADED,
                url: user.avatar_url,
            };
        }

        return {
            type: AVATAR_TYPES.GENERATED,
            generated: buildGeneratedAvatar({
                seed: user.avatar_seed,
                displayName,
                email: user.email,
            }),
        };
    }, [user, displayName]);

    const avatarContainerClasses = useMemo(
        () =>
            cn(
                'flex h-8 w-8 items-center justify-center overflow-hidden rounded-full text-sm font-semibold uppercase transition-shadow',
                avatarPresentation.type === AVATAR_TYPES.UPLOADED ? 'bg-black/40' : 'bg-[var(--primary)]/40',
                highlightAvatar &&
                    'ring-2 ring-cyan-300 shadow-[0_0_12px_rgba(34,211,238,0.55)] animate-pulse'
            ),
        [avatarPresentation.type, highlightAvatar]
    );

    useEffect(() => {
        setHasAvatarLoadError(false);
    }, [avatarPresentation.type, avatarPresentation.url]);

    const handleAvatarLoadError = useCallback(() => {
        setHasAvatarLoadError(true);
    }, []);

    const avatarContent = useMemo(() => {
        const shouldRenderUploaded =
            avatarPresentation.type === AVATAR_TYPES.UPLOADED &&
            avatarPresentation.url &&
            !hasAvatarLoadError;

        if (shouldRenderUploaded) {
            return (
                <img
                    src={avatarPresentation.url}
                    alt={displayName || 'Avatar utente'}
                    className="h-full w-full object-cover"
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    onError={handleAvatarLoadError}
                />
            );
        }

        const gradientStyle = avatarPresentation.generated?.gradient?.style || undefined;
        const computedInitials = (avatarPresentation.generated?.initials || initials) || 'GS';

        return (
            <span
                className="flex h-full w-full items-center justify-center text-sm font-semibold uppercase text-white"
                style={gradientStyle}
            >
                {computedInitials}
            </span>
        );
    }, [avatarPresentation, displayName, handleAvatarLoadError, hasAvatarLoadError, initials]);

    const handleNavigateToLogin = useCallback(() => {
        closeMenu();
        navigate('/login');
    }, [closeMenu, navigate]);

    const handleNavigateToSignup = useCallback(() => {
        closeMenu();
        navigate('/signup');
    }, [closeMenu, navigate]);

    const handleSelectAccountPrivacy = useCallback(() => {
        closeAccountMenu();
        closeMenu();
    }, [closeAccountMenu, closeMenu]);

    const handleSignOut = useCallback(async () => {
        if (isSigningOut) return;
        setIsSigningOut(true);
        try {
            await signOutAndClearPublicAuthIntent({ signOut });
            toast({ title: t('auth.logout_success') });
        } catch (error) {
            console.error('Logout error:', error);
            toast({
                title: t('auth.logout_error'),
                description: error?.message,
                variant: 'destructive',
            });
        } finally {
            setIsSigningOut(false);
            closeAccountMenu();
            closeMenu();
        }
    }, [closeAccountMenu, closeMenu, isSigningOut, signOut, toast, t]);

    const desktopAccountMenuProps = {
        user,
        t,
        displayName,
        avatarContainerClasses,
        avatarContent,
        isAccountMenuOpen,
        onToggleAccountMenu: toggleAccountMenu,
        onSignOut: handleSignOut,
        isSigningOut,
        accountMenuRef,
        onNavigateToSignup: handleNavigateToSignup,
        onNavigateToLogin: handleNavigateToLogin,
        onSelectAccountPrivacy: handleSelectAccountPrivacy,
        showSelectiveAccountStatus: isSelectivePublicAccount,
    };

    return (
        <div className="flex flex-col min-h-screen bg-[var(--background)] text-[var(--text)]">
            <SiteHeader
                navItems={navItems}
                activeHref={activeNavHref}
                isMenuOpen={isMenuOpen}
                onToggleMenu={toggleMenu}
                onCloseMenu={closeMenu}
                desktopAccountMenuProps={desktopAccountMenuProps}
                shouldDisableDrawer={shouldDisableDrawer}
                logoHref={landingHref}
            />

            <div className="fixed right-4 top-16 z-40 hidden lg:block">
                <LanguageSelector />
            </div>

            <main id="app-main-content" tabIndex={-1} className="flex-1 w-full pt-16 relative">
                <LegalAccountNotice userId={user?.id} />
                <div
                    className={cn("w-full", shouldMaskDashboard && "pointer-events-none blur-sm opacity-40")}
                >
                    {children}
                </div>
                {shouldMaskDashboard ? (
                    <div className="pointer-events-none absolute inset-0 bg-slate-950/70" aria-hidden="true" />
                ) : null}
            </main>

            <BackToTop
                blocked={isMenuOpen || isAccountMenuOpen || isDrawerOpen || isCookieBannerVisible || isCookiePreferencesOpen}
                label={t('common.back_to_top')}
                routeKey={`${location.pathname}${location.search}`}
            />

            <CookieControls
                isCookieBannerVisible={isCookieBannerVisible}
                isSelectivePublic={isSelectivePublicAccount}
                onOpenPreferences={handleOpenCookiePreferences}
                isCookiePreferencesOpen={isCookiePreferencesOpen}
                onClosePreferences={handleCloseCookiePreferences}
            />

            <Suspense fallback={null}>
                {shouldRenderLoginDrawer ? (
                    <LoginDrawer
                        title={drawerMeta.title}
                        description={drawerMeta.description}
                        contextualSections={drawerMeta.sections}
                        reviewComposer={drawerMeta.reviewComposer}
                        replyComposer={drawerMeta.replyComposer}
                        authIntent={drawerAuthIntent || drawerMeta.authIntent}
                        disabled={shouldDisableDrawer}
                        selectiveAccountMode={isSelectivePublicAccount}
                    />
                ) : null}
            </Suspense>

            {!shouldDisableDrawer && !shouldHideEdgeDrawerTrigger && !isDrawerOpen ? (
                <LoginDrawerTrigger variant="edge" />
            ) : null}

            <Toaster />
        </div>
    );
}

export default LayoutShell;
